import { jsx, jsxs } from 'react/jsx-runtime';
import * as React from 'react';
import { Swipeable } from 'react-swipeable';
import { CSSTransition } from 'react-transition-group';
import { throttle } from 'throttle-debounce';
import '../paging-button/index-chunk.esm.js';
import { c as classNames, l as liveAnnounce, L as Level } from '../theme-provider/index-chunk.esm.js';
import { E as EGDSTertiaryButton } from '../tertiary-button/index-chunk.esm.js';
import styles from '../styles/uitk-egds-toast.css';

const getClassNameList = classNames.bind(styles);
var TimerSetting;
(function (TimerSetting) {
    TimerSetting[TimerSetting["long"] = 8000] = "long";
    TimerSetting[TimerSetting["short"] = 4000] = "short";
})(TimerSetting || (TimerSetting = {}));
class EGDSToast extends React.PureComponent {
    static displayName = 'EGDSToast';
    static defaultProps = {
        show: false,
        autoHide: true,
        stacked: false,
    };
    timedToast;
    toastRef;
    actionRef;
    constructor(props) {
        super(props);
        this.state = {
            show: props.show,
            stateIsDerived: false,
        };
        this.toastRef = React.createRef();
        this.actionRef = React.createRef();
        this.dismissToast = this.dismissToast.bind(this);
        this.manuallyDismissToast = this.manuallyDismissToast.bind(this);
        this.clearTimer = this.clearTimer.bind(this);
        this.onEntered = this.onEntered.bind(this);
        this.onEntering = this.onEntering.bind(this);
        this.onExit = this.onExit.bind(this);
        this.onExited = this.onExited.bind(this);
        this.onExiting = this.onExiting.bind(this);
        this.startTimer = this.startTimer.bind(this);
        this.liveAnnounceMsg = this.liveAnnounceMsg.bind(this);
        this.handleScroll = throttle(100, this.handleScroll.bind(this));
    }
    componentDidUpdate() {
        const { show } = this.state;
        if (!show) {
            this.clearTimer();
        }
    }
    componentWillUnmount() {
        this.clearTimer();
        window.removeEventListener('scroll', this.handleScroll);
    }
    static getDerivedStateFromProps(nextProps, prevState) {
        const show = !prevState.stateIsDerived && !prevState.show ? false : nextProps.show;
        return {
            show,
            stateIsDerived: true,
        };
    }
    handleScroll() {
        window.removeEventListener('scroll', this.handleScroll);
        this.startTimer();
    }
    onExit() {
        const { props } = this;
        if (props.verticalOffset) {
            const offset = props.verticalOffset;
            this.toastRef.current.parentElement.style.transform = `translateY(-${offset + 150}px)`;
        }
    }
    onExited() {
        this.resetLiveAnnounceMsg();
    }
    onExiting() {
        const { props } = this;
        if (props.verticalOffset) {
            const offset = props.verticalOffset;
            this.toastRef.current.parentElement.style.transform = `translateY(${offset}px)`;
        }
    }
    onEntered() {
        this.setFocusAndLiveAnnounce();
        const { props } = this;
        if (props.verticalOffset) {
            const offset = props.verticalOffset;
            this.toastRef.current.parentElement.style.transform = `translateY(-${offset}px)`;
        }
    }
    onEntering() {
        const { props } = this;
        if (props.verticalOffset) {
            const offset = props.verticalOffset;
            this.toastRef.current.parentElement.style.transform = `translateY(-${offset + 150}px)`;
        }
    }
    setFocus(ref) {
        if (ref && ref.current) {
            ref.current.focus();
        }
    }
    setFocusAndLiveAnnounce() {
        const { actionCallback, actionText } = this.props;
        if (actionText && actionCallback) {
            this.setFocus(this.actionRef);
            // Listen for scroll event to start timer to
            // dismiss toast notification
            window.addEventListener('scroll', this.handleScroll);
        }
        else {
            this.liveAnnounceMsg();
            this.startTimer();
        }
    }
    clearTimer() {
        clearTimeout(this.timedToast);
    }
    startTimer() {
        const { props } = this;
        const timer = props.autoHideTimer || TimerSetting.short;
        // Before init setTimeout, clear the existing timer
        this.clearTimer();
        // Only dismiss the Toast automatically if autoHide is set to true
        if (props.autoHide) {
            this.timedToast = setTimeout(() => {
                this.dismissToast();
            }, timer);
        }
    }
    resetLiveAnnounceMsg() {
        liveAnnounce('', Level.POLITE);
    }
    liveAnnounceMsg() {
        const { children } = this.props;
        let message;
        React.Children.map(children, (child) => {
            if (child) {
                message = child;
            }
        });
        liveAnnounce(message, Level.POLITE);
    }
    isInViewport(element) {
        const { bottom, left, right, top } = element.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
        if (top >= 0 && left >= 0 && right <= viewportWidth && bottom <= viewportHeight) {
            return true;
        }
        return false;
    }
    hasManuallyFocused() {
        // Return true if any element besides the toast
        // and actionRef was manually set in focus
        if (document.activeElement &&
            document.activeElement !== this.toastRef.current &&
            document.activeElement !== this.actionRef.current &&
            document.activeElement.tagName !== 'BODY') {
            return true;
        }
        return false;
    }
    dismissToast(dismissedByAction) {
        const { onCloseRef, onDismiss } = this.props;
        const { show } = this.state;
        if (show && typeof onDismiss === 'function' && onDismiss) {
            onDismiss(dismissedByAction);
        }
        // Focus back to element when provided, has not manually set focus,
        // and the element is in the viewport
        if (onCloseRef && onCloseRef.current && this.isInViewport(onCloseRef.current) && !this.hasManuallyFocused()) {
            this.setFocus(onCloseRef);
        }
        // Clears timer before dismissing Toast
        this.clearTimer();
        // This is incredibly dangerous; state.show is being derived from props
        // setting it here will be overwritten when getDerivedStateFromProps is invoked by this state change
        this.setState({
            show: false,
            stateIsDerived: false,
        });
        window.removeEventListener('scroll', this.handleScroll);
    }
    manuallyDismissToast() {
        const { actionCallback } = this.props;
        if (actionCallback) {
            actionCallback();
        }
        this.dismissToast(true);
    }
    render() {
        const { actionCallback, actionText, children, stacked } = this.props;
        const { show } = this.state;
        const action = !!(actionText && actionCallback);
        const actionBtnParentClass = getClassNameList('uitk-toast-action-container');
        const toastWrapperClassName = getClassNameList('uitk-toast-inner-wrapper', {
            'uitk-toast-inner-wrapper-stacked': stacked,
        });
        const parentClasses = getClassNameList('uitk-toast', 'uitk-toast-slide');
        const labelClasses = getClassNameList('uitk-toast-content');
        const actionBtnA11yText = typeof children === 'string' ? `${children} ${actionText}` : actionText;
        return (jsx(CSSTransition, { classNames: {
                appear: getClassNameList('uitk-toast-appear'),
                appearActive: getClassNameList('uitk-toast-appear-active'),
                enter: getClassNameList('uitk-toast-enter'),
                enterActive: getClassNameList('uitk-toast-enter-active'),
                enterDone: getClassNameList('uitk-toast-enter-done'),
                exit: getClassNameList('uitk-toast-exit'),
                exitActive: getClassNameList('uitk-toast-exit-active'),
            }, delay: "0", duration: "500", appear: true, mountOnEnter: false, in: show, unmountOnExit: true, timeout: {
                enter: 300,
                exit: 500,
            }, onEntered: this.onEntered, onEntering: this.onEntering, onExit: this.onExit, onExited: this.onExited, onExiting: this.onExiting, children: jsx(Swipeable, { "data-control": "uitk-toast", className: parentClasses, onSwiped: this.manuallyDismissToast, children: jsxs("div", { className: toastWrapperClassName, ref: this.toastRef, onMouseEnter: this.clearTimer, onMouseLeave: this.startTimer, onFocus: this.clearTimer, onBlur: this.startTimer, children: [jsx("span", { className: labelClasses, children: children }), action && (jsx("div", { className: actionBtnParentClass, children: jsx(EGDSTertiaryButton, { type: "button", onClick: this.manuallyDismissToast, onMouseEnter: this.clearTimer, onMouseLeave: this.startTimer, onFocus: this.clearTimer, onBlur: this.startTimer, className: getClassNameList('uitk-toast-button'), domRef: this.actionRef, "aria-label": actionBtnA11yText, inverse: true, children: actionText }) }))] }) }) }));
    }
}

export { EGDSToast as E, TimerSetting as T };
//# sourceMappingURL=index-chunk.esm.js.map
